import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useToast } from '@shared/hooks/use-toast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMinimumFeeColumns } from '../../hooks/useMinimumFeeColumns';
import { useMinimumFeesMain } from '../../hooks/useMinimumFeesMain';
import { useInvoiceMinimumFees } from '../../hooks/useMinimumFees';
import { isMinimumFeeInvoiceable, MinimumFee } from '../../models/MinimumFee';
import { Breadcrumb, CardList, MinimumFeesAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection, SelectionToolbar } from '../interaction';

const DEFAULT_COLUMNS = [
  'team_name',
  'period',
  'minimum_fee',
  'actual_billed_amount',
  'storage_amount',
  'total_computed_amount',
  'difference_amount',
  'status',
  'invoice_id',
  'created_at',
];

export const Main = () => {
  const { t } = useTranslation('minimumFeesModule');
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useCurrency();
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const invoiceMutation = useInvoiceMinimumFees();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const [selectedFeeIds, setSelectedFeeIds] = useState<Set<string>>(new Set());

  const {
    filtersState,
    hasActiveFilters,
    handleFiltersChange,
    clearFilters,
    data: minimumFees,
    meta,
    isLoading,
    isFetchingNextPage,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useMinimumFeesMain();

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, isLoading]);

  useEffect(() => {
    setSelectedFeeIds(new Set());
  }, [filtersState.invoice_id, filtersState.month, filtersState.status, filtersState.team_id, filtersState.year]);

  const selectedFees = useMemo(
    () => minimumFees.filter((fee) => selectedFeeIds.has(fee.id)),
    [minimumFees, selectedFeeIds],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedFeeIds(new Set());
  }, []);

  const handleSelectionChanged = useCallback((ids: Set<string>) => {
    setSelectedFeeIds(ids);
  }, []);

  const handleToggleFeeSelection = useCallback((fee: MinimumFee) => {
    if (!isMinimumFeeInvoiceable(fee)) {
      return;
    }

    setSelectedFeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(fee.id)) {
        next.delete(fee.id);
      } else {
        next.add(fee.id);
      }
      return next;
    });
  }, []);

  const handleSelectAllFees = useCallback(() => {
    const invoiceableFees = minimumFees.filter(isMinimumFeeInvoiceable);
    const allSelected = invoiceableFees.length > 0 && invoiceableFees.every((fee) => selectedFeeIds.has(fee.id));

    if (allSelected) {
      setSelectedFeeIds(new Set());
      return;
    }

    setSelectedFeeIds(new Set(invoiceableFees.map((fee) => fee.id)));
  }, [minimumFees, selectedFeeIds]);

  const selectedTotal = useMemo(
    () => selectedFees.reduce((sum, fee) => sum + Number(fee.difference_amount || 0), 0),
    [selectedFees],
  );

  const formatMoney = useCallback(
    (value: number) => {
      const formatted = new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

      return `${formatted} ${currency}`;
    },
    [currency],
  );

  const handleViewInvoice = useCallback(
    (invoiceId: string) => {
      navigate(`/invoices/view/${invoiceId}`);
    },
    [navigate],
  );

  const { tanStackColumns } = useMinimumFeeColumns({
    selectedColumns,
    onViewInvoice: handleViewInvoice,
  });

  const activeFiltersCount = useMemo(() => Object.values(filtersState).filter(Boolean).length, [filtersState]);

  const validateSelection = useCallback(() => {
    if (selectedFees.length === 0) {
      toast({
        title: t('validation.emptySelectionTitle', 'Sin cuotas seleccionadas'),
        description: t('validation.emptySelection', 'Selecciona al menos una cuota pendiente.'),
        variant: 'destructive',
      });
      return false;
    }

    if (selectedFees.some((fee) => !isMinimumFeeInvoiceable(fee))) {
      toast({
        title: t('validation.invalidSelectionTitle', 'Selección inválida'),
        description: t('validation.invalidSelection', 'Solo pueden facturarse cuotas con estado pendiente.'),
        variant: 'destructive',
      });
      return false;
    }

    return true;
  }, [selectedFees, t, toast]);

  const handleInvoiceSelected = useCallback(() => {
    if (!validateSelection()) return;

    confirmModalRef.current?.open(
      async () => {
        if (!validateSelection()) return;

        try {
          await invoiceMutation.mutateAsync(selectedFees.map((fee) => fee.id));
          setSelectedFeeIds(new Set());
          await refetch();
          toast({
            title: t('toasts.invoiceSuccessTitle', 'Factura generada'),
            description: t(
              'toasts.invoiceSuccess',
              'Las cuotas mínimas seleccionadas fueron facturadas correctamente.',
            ),
          });
        } catch (mutationError) {
          toast({
            title: t('toasts.invoiceErrorTitle', 'No se pudo facturar'),
            description:
              mutationError instanceof Error
                ? mutationError.message
                : t('toasts.invoiceError', 'Error inesperado al facturar cuotas mínimas.'),
            variant: 'destructive',
          });
        }
      },
      undefined,
      t('confirm.title', 'Facturar cuotas mínimas'),
      t('confirm.message', 'Se generará una factura para las cuotas mínimas seleccionadas.'),
      t('confirm.confirm', 'Facturar'),
      t('confirm.cancel', 'Cancelar'),
      () => (
        <div className="rounded-md border border-card-border bg-card-bg p-3 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-complementary-text">{t('confirm.count', 'Cantidad de cuotas')}</span>
            <span className="font-medium text-title">{selectedFees.length}</span>
          </div>
          <div className="mt-2 flex justify-between gap-4">
            <span className="text-complementary-text">{t('confirm.total', 'Importe total agregado')}</span>
            <span className="font-medium text-title">{formatMoney(selectedTotal)}</span>
          </div>
        </div>
      ),
    );
  }, [formatMoney, invoiceMutation, refetch, selectedFees, selectedTotal, t, toast, validateSelection]);

  const renderMinimumFees = () => {
    if (isMobile) {
      return (
        <>
          <CardList
            fees={minimumFees}
            selectedColumns={selectedColumns}
            onViewInvoice={handleViewInvoice}
            selectedFeeIds={selectedFeeIds}
            onToggleFeeSelection={handleToggleFeeSelection}
            onSelectAllFees={handleSelectAllFees}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            total={meta.total}
          />
          <SelectionToolbar
            selectedCount={selectedFeeIds.size}
            onClose={handleClearSelection}
            onInvoice={handleInvoiceSelected}
            isInvoicing={invoiceMutation.isPending}
          />
        </>
      );
    }

    return (
      <MinimumFeesAgGridTable
        fees={minimumFees}
        selectedColumns={selectedColumns}
        onViewInvoice={handleViewInvoice}
        selectedFeeIds={selectedFeeIds}
        onSelectionChanged={handleSelectionChanged}
        onClearSelection={handleClearSelection}
        onInvoiceSelected={handleInvoiceSelected}
        isInvoicing={invoiceMutation.isPending}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    );
  };

  if (isInitialLoad && isLoading) {
    return <LoadingState message={t('loading.message', 'Cargando cuotas mínimas...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="px-6 pt-4">
      <Breadcrumb />

      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              filters={filtersState}
              onFiltersChange={handleFiltersChange}
              onClearFilters={clearFilters}
              activeFiltersCount={activeFiltersCount}
              columns={tanStackColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={setSelectedColumns}
            />
          </div>
          <TotalResults total={meta.total} />
        </div>
      </div>

      {!isInitialLoad && minimumFees.length === 0 && (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.title', 'No existen cuotas mínimas para los filtros seleccionados.')}
          description=""
        />
      )}

      {!isInitialLoad && meta.total > 0 && <div className="mb-4 mt-4">{renderMinimumFees()}</div>}

      <ConfirmModal ref={confirmModalRef} />
    </div>
  );
};
